<div class="wizard-step-content">
  <h3 class="mb-4">
    <i class="fa fa-calendar-alt text-primary me-2"></i>
    {{ 'eventWizard.schedule.title' | translate }}
  </h3>
  <p class="text-muted mb-6">{{ 'eventWizard.schedule.subtitle' | translate }}</p>

  @if (loading) {
    <div class="d-flex justify-content-center py-10">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
      </div>
    </div>
  } @else if (classes.length === 0) {
    <div class="alert alert-warning">
      <i class="fa fa-exclamation-triangle me-2"></i>
      {{ 'eventWizard.schedule.noClassesFound' | translate }}
    </div>
  } @else {
    <!-- Day Tabs Navigation -->
    @if (eventDays.length > 0) {
      <div class="day-tabs-wrapper mb-4">
        <div class="day-tabs">
          @for (day of eventDays; track day.date.getTime()) {
            <button class="day-tab"
                    [class.active]="selectedDay && isSameDay(selectedDay, day.date)"
                    [class.today]="day.isToday"
                    (click)="selectDay(day)">
              <span class="day-label">{{ day.label }}</span>
              <span class="day-label-short">{{ day.shortLabel }}</span>
              @if (getClassCountForDay(day) > 0) {
                <span class="day-count">{{ getClassCountForDay(day) }}</span>
              }
            </button>
          }
        </div>
      </div>
    }

    @if (filteredClasses.length === 0) {
      <div class="empty-day-message text-center py-10">
        <i class="fa fa-calendar-xmark fs-2x text-muted mb-3 d-block"></i>
        <p class="text-muted mb-0">{{ 'eventWizard.schedule.noClassesForDay' | translate }}</p>
        <small class="text-muted">{{ 'eventWizard.schedule.selectAnotherDay' | translate }}</small>
      </div>
    } @else {
      <div class="classes-schedule-list">
        @for (eventClass of filteredClasses; track eventClass.event_class_id) {
          <div class="class-section">
            <!-- Class Header Row -->
            <div class="class-row">
              <div class="class-time">
                <span class="time-badge">{{ getClassStartTime(eventClass) }}</span>
              </div>
              <div class="class-info">
                <h5 class="mb-0">{{ eventClass.class_name }}</h5>
                <span class="class-stats text-muted">
                  {{ 'eventWizard.schedule.schedulesCount' | translate:{ count: getSchedulesForClass(eventClass.event_class_id).length } }}
                </span>
              </div>
              <div class="class-actions position-relative">
                <button class="btn btn-sm btn-primary" (click)="toggleTypeSelector(eventClass.event_class_id)">
                  <i class="fa fa-plus me-1"></i>{{ 'eventWizard.schedule.addSchedule' | translate }}
                </button>

                <!-- Schedule Type Selector Dropdown -->
                @if (showTypeSelector === eventClass.event_class_id) {
                  <div class="schedule-type-selector">
                    @for (type of scheduleTypes; track type.value) {
                      <div class="schedule-type-option"
                           [class.disabled]="type.disabled"
                           (click)="!type.disabled && createScheduleWithType(eventClass, type); $event.stopPropagation()">
                        <i class="fa {{ type.icon }} me-2"></i>
                        <div class="flex-grow-1">
                          <strong>{{ type.label }}</strong>
                          @if (type.comingSoon) {
                            <span class="badge bg-secondary ms-2">{{ 'eventWizard.schedule.comingSoon' | translate }}</span>
                          }
                          <small class="d-block text-muted">{{ type.description }}</small>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>

            <!-- Schedules for this class -->
            @if (hasSchedules(eventClass.event_class_id)) {
              <div class="schedules-list">
                @for (schedule of getSchedulesForClass(eventClass.event_class_id); track schedule.schedule_id) {
                  <div class="schedule-row">
                    <i class="fa {{ getScheduleTypeIcon(schedule) }} schedule-icon"></i>
                    <div class="schedule-info">
                      <span class="schedule-name">{{ schedule.schedule_name }}</span>
                      <span class="schedule-meta text-muted">
                        {{ getEntryCount(schedule) }} {{ schedule.session_type === 'group' ? ('eventWizard.schedule.participants' | translate) : ('eventWizard.schedule.entries' | translate) }}
                        · {{ formatDuration(schedule.time_per_entry_seconds) }}
                      </span>
                    </div>
                    <span class="badge" [class]="'badge-light-' + (schedule.status === 'draft' ? 'secondary' : schedule.status === 'published' ? 'info' : schedule.status === 'live' ? 'success' : 'primary')">
                      {{ schedule.status }}
                    </span>
                    <div class="schedule-actions">
                      <button class="btn btn-sm btn-icon btn-light-primary" (click)="openScheduleEditor(schedule, eventClass)" [title]="'common.edit' | translate">
                        <i class="fa fa-edit"></i>
                      </button>
                      <button class="btn btn-sm btn-icon btn-light-danger" (click)="deleteSchedule(schedule, eventClass)" [title]="'common.delete' | translate">
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                }
              </div>
            } @else {
              <div class="no-schedules text-center text-muted py-4">
                <i class="fa fa-stream me-2"></i>
                <span>{{ 'eventWizard.schedule.noSchedulesYet' | translate }}</span>
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- Summary -->
    <div class="alert alert-light-info mt-4">
      <div class="d-flex align-items-center">
        <i class="fa fa-info-circle text-info fs-2 me-3"></i>
        <div>
          <strong>{{ 'eventWizard.schedule.schedulesCreated' | translate:{ count: totalScheduleCount } }}</strong>
          <div class="text-muted">{{ 'eventWizard.schedule.schedulesHelp' | translate }}</div>
        </div>
      </div>
    </div>
  }
</div>

@if (showTypeSelector !== null) {
  <div class="backdrop" (click)="showTypeSelector = null"></div>
}
